import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  FaInstagram, FaFacebook, FaLinkedin, FaXTwitter, FaTiktok,
} from "react-icons/fa6";
import { API } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

// ── Platform config ───────────────────────────────────────────────────────────
const PLATFORMS = [
  { id: "instagram", label: "Instagram", Icon: FaInstagram, color: "#E1306C" },
  { id: "facebook",  label: "Facebook",  Icon: FaFacebook,  color: "#1877F2" },
  { id: "linkedin",  label: "LinkedIn",  Icon: FaLinkedin,  color: "#0A66C2" },
  { id: "x",         label: "X",         Icon: FaXTwitter,  color: "#000000" },
  { id: "tiktok",    label: "TikTok",    Icon: FaTiktok,    color: "#010101" },
];

const STATUSES = [
  { id: "draft",     label: "Borrador",   color: "#6B7280", bg: "#F3F4F6", border: "#D1D5DB" },
  { id: "scheduled", label: "Programado", color: "#1D4ED8", bg: "#EFF6FF", border: "#93C5FD" },
  { id: "published", label: "Publicado",  color: "#065F46", bg: "#ECFDF5", border: "#6EE7B7" },
  { id: "cancelled", label: "Cancelado",  color: "#991B1B", bg: "#FEF2F2", border: "#FCA5A5" },
  { id: "failed",    label: "Fallido",    color: "#7C2D12", bg: "#FFF7ED", border: "#FDBA74" },
];

// ── Content types ─────────────────────────────────────────────────────────────
// Feed slot: mutually exclusive (radio). Story slot: combinable (checkbox).
// For LinkedIn/X/TikTok: single select only.
const FEED_SLOT = {
  instagram: ["post_image", "post_video", "reel", "carousel"],
  facebook:  ["post", "post_video", "reel"],
};
const STORY_SLOT = {
  instagram: ["story"],
  facebook:  ["story"],
};

const CONTENT_TYPES = {
  instagram: [
    { id: "post_image", label: "Post imagen",  slot: "feed" },
    { id: "post_video", label: "Post video",   slot: "feed" },
    { id: "reel",       label: "Reel",         slot: "feed" },
    { id: "carousel",   label: "Carousel",     slot: "feed" },
    { id: "story",      label: "Story",        slot: "story" },
  ],
  facebook: [
    { id: "post",       label: "Post",         slot: "feed" },
    { id: "post_video", label: "Post video",   slot: "feed" },
    { id: "reel",       label: "Reel",         slot: "feed" },
    { id: "story",      label: "Story",        slot: "story" },
  ],
  linkedin: [
    { id: "post",       label: "Post" },
    { id: "post_image", label: "Post imagen" },
    { id: "document",   label: "Documento PDF" },
  ],
  x: [
    { id: "tweet",       label: "Tweet" },
    { id: "tweet_media", label: "Tweet con media" },
  ],
  tiktok: [
    { id: "video",        label: "Video" },
    { id: "photo_series", label: "Serie de fotos" },
  ],
};

// ── Field rules per content type ──────────────────────────────────────────────
// caption, hashtags, postUrl: show/hide
// maxMedia: max files (1 = single, N = multi)
// mediaType: "image" | "video" | "any" | null
// orderable: drag-to-reorder
const FIELD_RULES = {
  instagram: {
    post_image: { caption: true,  hashtags: true,  postUrl: true,  maxMedia: 1,  mediaType: "image", orderable: false },
    post_video: { caption: true,  hashtags: true,  postUrl: true,  maxMedia: 1,  mediaType: "video", orderable: false },
    reel:       { caption: true,  hashtags: true,  postUrl: true,  maxMedia: 1,  mediaType: "video", orderable: false },
    carousel:   { caption: true,  hashtags: true,  postUrl: true,  maxMedia: 10, mediaType: "any",   orderable: true  },
    story:      { caption: false, hashtags: false, postUrl: false, maxMedia: 1,  mediaType: "any",   orderable: false },
  },
  facebook: {
    post:       { caption: true,  hashtags: true,  postUrl: true,  maxMedia: 1,  mediaType: "image", orderable: false },
    post_video: { caption: true,  hashtags: false, postUrl: true,  maxMedia: 1,  mediaType: "video", orderable: false },
    reel:       { caption: true,  hashtags: false, postUrl: true,  maxMedia: 1,  mediaType: "video", orderable: false },
    story:      { caption: false, hashtags: false, postUrl: false, maxMedia: 1,  mediaType: "any",   orderable: false },
  },
  linkedin: {
    post:       { caption: true,  hashtags: true,  postUrl: true,  maxMedia: 0,  mediaType: null,    orderable: false },
    post_image: { caption: true,  hashtags: true,  postUrl: true,  maxMedia: 1,  mediaType: "image", orderable: false },
    document:   { caption: true,  hashtags: true,  postUrl: true,  maxMedia: 1,  mediaType: "document", orderable: false },
  },
  x: {
    tweet:       { caption: true,  hashtags: false, postUrl: true,  maxMedia: 0,  mediaType: null,    orderable: false },
    tweet_media: { caption: true,  hashtags: false, postUrl: true,  maxMedia: 4,  mediaType: "any",   orderable: false },
  },
  tiktok: {
    video:        { caption: true,  hashtags: false, postUrl: false, maxMedia: 1,  mediaType: "video", orderable: false },
    photo_series: { caption: true,  hashtags: false, postUrl: false, maxMedia: 35, mediaType: "image", orderable: true  },
  },
};

// Compute effective field visibility + media constraints from selected types
function computeEffective(platforms, contentTypes) {
  let caption = false, hashtags = false, postUrl = false;
  let maxMedia = 0, orderable = false;
  const mediaTypes = new Set();

  for (const pid of platforms) {
    const types = normalizeTypes(contentTypes[pid], pid);
    for (const ct of types) {
      const r = FIELD_RULES[pid]?.[ct];
      if (!r) continue;
      if (r.caption)   caption   = true;
      if (r.hashtags)  hashtags  = true;
      if (r.postUrl)   postUrl   = true;
      if (r.maxMedia > maxMedia) maxMedia = r.maxMedia;
      if (r.orderable) orderable = true;
      if (r.mediaType && r.mediaType !== null) mediaTypes.add(r.mediaType);
    }
  }

  // Determine accept string for file input
  let accept = "";
  if (mediaTypes.has("any") || (mediaTypes.has("image") && mediaTypes.has("video"))) {
    accept = "image/*,video/mp4,video/quicktime,video/webm";
  } else if (mediaTypes.has("image")) {
    accept = "image/*";
  } else if (mediaTypes.has("video")) {
    accept = "video/mp4,video/quicktime,video/webm";
  } else if (mediaTypes.has("document")) {
    accept = "application/pdf";
  }

  return { caption, hashtags, postUrl, maxMedia, orderable, accept };
}

// Toggle content type with slot logic
function toggleContentType(currentTypes, typeId, platformId) {
  const cfg = CONTENT_TYPES[platformId];
  if (!cfg) return currentTypes;

  const typeDef = cfg.find(t => t.id === typeId);
  if (!typeDef) return currentTypes;

  // Single-select platforms (LinkedIn, X, TikTok)
  if (!FEED_SLOT[platformId]) return [typeId];

  const isSelected = currentTypes.includes(typeId);

  if (typeDef.slot === "story") {
    // Toggle story independently — can't deselect if it's the only one
    if (isSelected && currentTypes.length === 1) return currentTypes;
    return isSelected
      ? currentTypes.filter(t => t !== typeId)
      : [...currentTypes, typeId];
  }

  // Feed slot: replace any existing feed type, keep story
  const storyTypes = STORY_SLOT[platformId] || [];
  const currentStory = currentTypes.filter(t => storyTypes.includes(t));

  if (isSelected) {
    // Deselect — keep story if still selected, but need at least one type
    const remaining = currentTypes.filter(t => t !== typeId);
    return remaining.length > 0 ? remaining : currentTypes;
  }
  // Select new feed type — replace existing feed type
  return [typeId, ...currentStory];
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function normalizeTypes(val, platformId) {
  if (Array.isArray(val)) return val;
  if (val) return [val];
  return [CONTENT_TYPES[platformId]?.[0]?.id].filter(Boolean);
}

function localToUTC(s) { return s ? new Date(s).toISOString() : null; }
function utcToLocal(s) {
  if (!s) return "";
  const d = new Date(s);
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const PLATFORM_MAP = Object.fromEntries(PLATFORMS.map(p => [p.id, p]));
const STATUS_MAP   = Object.fromEntries(STATUSES.map(s => [s.id, s]));
const DAYS_ES   = ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"];
const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const MAX_IMAGE_PX = 1920;
const JPEG_QUALITY = 0.82;

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return m;
}

function fmtDateTime(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("es-AR", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
}
function fmtTime(dt) {
  if (!dt) return "";
  return new Date(dt).toLocaleTimeString("es-AR", { hour:"2-digit", minute:"2-digit" });
}

async function compressImage(file) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  return new Promise(resolve => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > MAX_IMAGE_PX || height > MAX_IMAGE_PX) {
        const ratio = Math.min(MAX_IMAGE_PX / width, MAX_IMAGE_PX / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => {
        if (!blob || blob.size >= file.size) { resolve(file); return; }
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
      }, "image/jpeg", JPEG_QUALITY);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────
function StatusBadge({ status, small }) {
  const s = STATUS_MAP[status] || STATUS_MAP.draft;
  return (
    <span style={{
      fontSize: small ? 10 : 11, fontWeight: 600,
      padding: small ? "1px 6px" : "2px 9px", borderRadius: 10,
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      display: "inline-flex", alignItems: "center", whiteSpace: "nowrap",
    }}>{s.label}</span>
  );
}

function PlatformIcons({ platforms, size = 14, gap = 4 }) {
  return (
    <div style={{ display: "flex", gap, alignItems: "center" }}>
      {(platforms || []).map(pid => {
        const p = PLATFORM_MAP[pid];
        return p ? <p.Icon key={pid} size={size} color={p.color} title={p.label} /> : null;
      })}
    </div>
  );
}

// ── Drag & drop orderable media uploader ──────────────────────────────────────
function MediaUploader({ mediaUrls, onChange, maxFiles = 1, accept = "image/*,video/mp4,video/quicktime,video/webm", orderable = false }) {
  const toast = useToast();
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState([]);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragSrcIdx = useRef(null);
  const inputRef = useRef();

  const canAddMore = maxFiles === 0 ? false : mediaUrls.length < maxFiles;

  const uploadFiles = async (files) => {
    const toUpload = Array.from(files).slice(0, maxFiles - mediaUrls.length);
    if (toUpload.length === 0) return;
    setUploading(toUpload.map(f => f.name));
    for (const file of toUpload) {
      try {
        const compressed = await compressImage(file);
        const fd = new FormData();
        fd.append("file", compressed);
        const r = await API.post("/api/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange(prev => [...prev, r.data.url]);
      } catch (err) {
        toast(err.response?.data?.error || `Error subiendo ${file.name}`, "error");
      }
    }
    setUploading([]);
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files); };
  const removeUrl = (url) => onChange(prev => prev.filter(u => u !== url));

  // Drag-to-reorder handlers
  const onDragStart = (idx) => { dragSrcIdx.current = idx; };
  const onDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const onDropItem = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIdx(null);
    const src = dragSrcIdx.current;
    if (src === null || src === idx) return;
    onChange(prev => {
      const arr = [...prev];
      const [item] = arr.splice(src, 1);
      arr.splice(idx, 0, item);
      return arr;
    });
    dragSrcIdx.current = null;
  };

  return (
    <div>
      {/* Drop zone — only show if can add more */}
      {maxFiles > 0 && canAddMore && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? "var(--accent)" : "var(--border)"}`,
            borderRadius: "var(--radius-lg)", padding: "18px 16px", textAlign: "center",
            background: dragging ? "var(--accent-dim)" : "var(--bg-elevated)",
            cursor: "pointer", transition: "all .15s",
            marginBottom: mediaUrls.length > 0 || uploading.length > 0 ? 10 : 0,
          }}
        >
          <div style={{ fontSize: 20, marginBottom: 4 }}>📎</div>
          <div style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500 }}>
            {maxFiles === 1 ? "Arrastrá o hacé clic para seleccionar" : `Arrastrá o hacé clic — ${mediaUrls.length}/${maxFiles} archivos`}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
            Las imágenes se comprimen automáticamente · Máx. 50 MB
          </div>
          <input ref={inputRef} type="file" multiple={maxFiles > 1} accept={accept}
            style={{ display: "none" }} onChange={e => uploadFiles(e.target.files)} />
        </div>
      )}

      {/* Uploading */}
      {uploading.map((name, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
          background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", marginBottom: 4,
          border: "1px solid var(--border)" }}>
          <div className="spinner" style={{ width: 14, height: 14, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Subiendo {name}...</span>
        </div>
      ))}

      {/* Uploaded files */}
      {mediaUrls.map((url, idx) => {
        const isImg = /\.(jpg|jpeg|png|webp|gif)$/i.test(url);
        const filename = url.split("/").pop();
        const isDragTarget = dragOverIdx === idx;
        return (
          <div key={url}
            draggable={orderable}
            onDragStart={orderable ? () => onDragStart(idx) : undefined}
            onDragOver={orderable ? e => onDragOver(e, idx) : undefined}
            onDragLeave={orderable ? () => setDragOverIdx(null) : undefined}
            onDrop={orderable ? e => onDropItem(e, idx) : undefined}
            style={{
              display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
              background: isDragTarget ? "var(--accent-dim)" : "var(--bg-elevated)",
              borderRadius: "var(--radius-md)", marginBottom: 4,
              border: `1px solid ${isDragTarget ? "var(--accent)" : "var(--border)"}`,
              cursor: orderable ? "grab" : "default",
              transition: "background .1s, border-color .1s",
            }}
          >
            {orderable && (
              <span style={{ color: "var(--text-muted)", fontSize: 12, flexShrink: 0, userSelect: "none" }}>⠿</span>
            )}
            {orderable && (
              <span style={{ fontSize: 11, color: "var(--text-muted)", flexShrink: 0, minWidth: 18, textAlign: "center" }}>{idx + 1}</span>
            )}
            {isImg ? (
              <img src={url} alt={filename}
                style={{ width: 36, height: 36, objectFit: "cover", borderRadius: "var(--radius-sm)", flexShrink: 0 }} />
            ) : (
              <div style={{ width: 36, height: 36, background: "var(--bg-surface)", borderRadius: "var(--radius-sm)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>🎬</div>
            )}
            <a href={url} target="_blank" rel="noreferrer"
              style={{ flex: 1, fontSize: 11, color: "var(--accent)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {filename}
            </a>
            <button onClick={() => removeUrl(url)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--danger)", fontSize: 14, flexShrink: 0 }}>✕</button>
          </div>
        );
      })}
      {orderable && mediaUrls.length > 1 && (
        <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
          ⠿ Arrastrá para reordenar
        </p>
      )}
    </div>
  );
}

// ── Media preview modal ───────────────────────────────────────────────────────
function MediaPreviewModal({ url, onClose }) {
  const isVid = /\.(mp4|mov|webm|avi)$/i.test(url);
  useEffect(() => {
    const h = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)",
      zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh" }}>
        <button onClick={onClose} style={{
          position: "absolute", top: -36, right: 0, background: "none", border: "none",
          color: "#fff", fontSize: 22, cursor: "pointer", lineHeight: 1,
        }}>✕</button>
        {isVid ? (
          <video src={url} controls autoPlay style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 8, display: "block" }} />
        ) : (
          <img src={url} alt="" style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 8, objectFit: "contain", display: "block" }} />
        )}
        <a href={url} target="_blank" rel="noreferrer" style={{
          display: "block", textAlign: "center", marginTop: 8, fontSize: 11, color: "rgba(255,255,255,0.6)",
          textDecoration: "none",
        }}>Abrir en nueva pestaña →</a>
      </div>
    </div>
  );
}

// ── Table row ─────────────────────────────────────────────────────────────────
function PostRow({ post, onEdit, onDelete, onUpdate }) {
  const toast = useToast();
  const [publishing, setPublishing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handlePublishNow = async (e) => {
    e.stopPropagation();
    if (!window.confirm("¿Publicar ahora?")) return;
    setPublishing(true);
    try {
      const r = await API.post(`/api/social-scheduler/posts/${post.id}/publish`);
      toast(r.data.status === "published" ? "✅ Publicado" : "⚠️ Publicado con errores", r.data.status === "published" ? "success" : "error");
      onUpdate({ ...post, status: r.data.status, publish_results: r.data.results, published_at: new Date().toISOString() });
    } catch (err) {
      toast(err.response?.data?.error || "Error al publicar", "error");
    } finally { setPublishing(false); }
  };

  // Gather content type labels
  const typeLabels = [];
  for (const pid of (post.platforms || [])) {
    const types = normalizeTypes(post.content_types?.[pid], pid);
    for (const ct of types) {
      const lbl = CONTENT_TYPES[pid]?.find(t => t.id === ct)?.label;
      if (lbl) typeLabels.push(lbl);
    }
  }

  // Gather publish results summary
  const results = post.publish_results || {};
  const resultBadges = [];
  for (const [plt, typeResults] of Object.entries(results)) {
    const p = PLATFORM_MAP[plt];
    if (typeResults?._error) {
      resultBadges.push({ ok: false, label: typeResults._error, p });
      continue;
    }
    for (const [ct, r] of Object.entries(typeResults || {})) {
      const ctLbl = CONTENT_TYPES[plt]?.find(t => t.id === ct)?.label || ct;
      resultBadges.push({ ok: r.ok, label: r.ok ? ctLbl : `${ctLbl}: ${r.error?.substring(0, 30)}`, p });
    }
  }

  const td = { padding: "10px 14px", borderBottom: "1px solid var(--border)", verticalAlign: "middle", fontSize: 13 };

  return (
    <tr style={{ background: "var(--bg-surface)" }}
      onMouseEnter={e => e.currentTarget.style.background = "var(--bg-hover)"}
      onMouseLeave={e => e.currentTarget.style.background = "var(--bg-surface)"}
    >
      {/* Cliente */}
      <td style={{ ...td, whiteSpace: "nowrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: post.client_color || "var(--accent)", flexShrink: 0 }} />
          <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{post.client_name}</span>
        </div>
      </td>

      {/* Plataformas */}
      <td style={{ ...td, whiteSpace: "nowrap" }}>
        <PlatformIcons platforms={post.platforms} size={15} gap={6} />
      </td>

      {/* Tipo de publicación */}
      <td style={td}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {typeLabels.map((lbl, i) => (
            <span key={i} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 6,
              background: "var(--bg-elevated)", border: "1px solid var(--border)",
              color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
              {lbl}
            </span>
          ))}
        </div>
      </td>

      {/* Media thumbnails */}
      <td style={{ ...td, maxWidth: 120 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(post.media_urls || []).slice(0, 4).map((url, i) => {
            const isVid = /\.(mp4|mov|webm|avi)$/i.test(url);
            return (
              <button key={i} onClick={() => setPreviewUrl(url)}
                title="Ver archivo"
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", position: "relative" }}>
                {isVid ? (
                  <div style={{
                    width: 36, height: 36, borderRadius: "var(--radius-sm)",
                    background: "var(--bg-elevated)", border: "1px solid var(--border)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                  }}>🎬</div>
                ) : (
                  <img src={url} alt="" style={{
                    width: 36, height: 36, objectFit: "cover", borderRadius: "var(--radius-sm)",
                    border: "1px solid var(--border)", display: "block",
                  }} />
                )}
                {i === 3 && (post.media_urls || []).length > 4 && (
                  <div style={{
                    position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)",
                    borderRadius: "var(--radius-sm)", display: "flex", alignItems: "center",
                    justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 700,
                  }}>+{(post.media_urls || []).length - 4}</div>
                )}
              </button>
            );
          })}
          {(post.media_urls || []).length === 0 && <span style={{ color: "var(--text-muted)", fontSize: 11 }}>—</span>}
        </div>
        {previewUrl && <MediaPreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />}
      </td>

      {/* Caption */}
      <td style={{ ...td, maxWidth: 200 }}>
        <span style={{
          display: "block", fontSize: 10, color: "var(--text-secondary)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          lineHeight: 1.4,
        }} title={post.caption || ""}>
          {post.caption || <span style={{ color: "var(--text-muted)" }}>—</span>}
        </span>
        {post.hashtags && (
          <span style={{ display: "block", fontSize: 10, color: "#6366F1", marginTop: 2,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {post.hashtags}
          </span>
        )}
      </td>

      {/* Fecha */}
      <td style={{ ...td, whiteSpace: "nowrap", color: "var(--text-secondary)" }}>
        {fmtDateTime(post.scheduled_at)}
      </td>

      {/* Estado + resultados */}
      <td style={{ ...td, whiteSpace: "nowrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <StatusBadge status={post.status} small />
          {resultBadges.map((b, i) => (
            <span key={i} style={{
              fontSize: 10, padding: "1px 6px", borderRadius: 6,
              background: b.ok ? "#ECFDF5" : "#FFF7ED",
              border: `1px solid ${b.ok ? "#6EE7B7" : "#FDBA74"}`,
              color: b.ok ? "#065F46" : "#7C2D12", whiteSpace: "nowrap",
              display: "flex", alignItems: "center", gap: 3,
            }}>
              {b.p && <b.p.Icon size={9} color={b.p.color} />}
              {b.ok ? `✓ ${b.label}` : `✗ ${b.label}`}
            </span>
          ))}
        </div>
      </td>

      {/* Notas */}
      <td style={{ ...td, maxWidth: 160, color: "var(--text-muted)" }}>
        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {post.notes || "—"}
        </span>
      </td>

      {/* Acciones */}
      <td style={{ ...td, whiteSpace: "nowrap" }}>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          {(post.status === "scheduled" || post.status === "failed" || post.status === "draft") && (
            <button className="btn btn-primary btn-sm" onClick={handlePublishNow} disabled={publishing}
              style={{ fontSize: 10, padding: "3px 8px" }}>
              {publishing ? "..." : "▶"}
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => onEdit(post)} title="Editar" style={{ padding: "4px 8px" }}>✏️</button>
          <button className="btn btn-ghost btn-sm" onClick={() => onDelete(post.id)} title="Eliminar"
            style={{ padding: "4px 8px", color: "var(--danger)" }}>✕</button>
        </div>
      </td>
    </tr>
  );
}

// ── Calendar view ─────────────────────────────────────────────────────────────
function CalendarView({ posts, year, month, selectedDay, onDayClick, onPostClick }) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const postsByDay = {};
  for (const p of posts) {
    if (!p.scheduled_at) continue;
    const d = new Date(p.scheduled_at);
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate();
      if (!postsByDay[day]) postsByDay[day] = [];
      postsByDay[day].push(p);
    }
  }
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const isToday = d => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3, marginBottom: 4 }}>
        {DAYS_ES.map(day => (
          <div key={day} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: "var(--text-muted)",
            padding: "6px 0", textTransform: "uppercase", letterSpacing: ".06em" }}>{day}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} style={{ aspectRatio: "1", minHeight: 80 }} />;
          const dayPosts = postsByDay[d] || [];
          const isSelected = selectedDay === d;
          return (
            <button key={d} onClick={() => onDayClick(d)} style={{
              aspectRatio: "1", minHeight: 80, padding: "8px 6px", borderRadius: "var(--radius-md)",
              background: isSelected ? "var(--accent-dim)" : isToday(d) ? "var(--bg-elevated)" : "var(--bg-surface)",
              border: isSelected || isToday(d) ? "1.5px solid var(--accent)" : "1px solid var(--border)",
              cursor: "pointer", textAlign: "left", display: "flex", flexDirection: "column", gap: 3,
              transition: "background 0.12s",
            }}>
              <div style={{ fontSize: 12, fontWeight: isToday(d) ? 700 : 500,
                color: isToday(d) ? "var(--accent)" : "var(--text-secondary)" }}>{d}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, overflow: "hidden" }}>
                {dayPosts.slice(0, 3).map(p => {
                  const pid = p.platforms?.[0];
                  const pc = PLATFORM_MAP[pid];
                  return (
                    <button key={p.id} onClick={e => { e.stopPropagation(); onPostClick(p); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 3, fontSize: 9, fontWeight: 600,
                        padding: "1px 4px", borderRadius: 4, background: `${p.client_color || "#0b00f9"}22`,
                        border: `1px solid ${p.client_color || "#0b00f9"}44`, color: "var(--text-primary)",
                        cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        maxWidth: "100%", fontFamily: "inherit",
                      }} title={`${p.client_name} · ${fmtTime(p.scheduled_at)}`}>
                      {pc && <pc.Icon size={8} color={pc.color} />}
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {fmtTime(p.scheduled_at)} {p.client_name}
                      </span>
                    </button>
                  );
                })}
                {dayPosts.length > 3 && (
                  <span style={{ fontSize: 9, color: "var(--text-muted)", paddingLeft: 4 }}>+{dayPosts.length - 3} más</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: ".06em",
  color: "var(--text-muted)", marginBottom: 6,
};

// ── Post Modal ────────────────────────────────────────────────────────────────
function PostModal({ post, clients, onClose, onSave }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [publishMode, setPublishMode] = useState(
    post?.status === "scheduled" ? "schedule" : "draft"
  );
  const [form, setForm] = useState({
    client_id:     post?.client_id || "",
    platforms:     post?.platforms || [],
    content_types: post?.content_types || {},
    caption:       post?.caption || "",
    hashtags:      post?.hashtags || "",
    media_urls:    post?.media_urls || [],
    scheduled_at:  utcToLocal(post?.scheduled_at),
    notes:         post?.notes || "",
    post_url:      post?.post_url || "",
  });

  const selectedClient = clients.find(c => c.id === parseInt(form.client_id));
  const connectedPlatforms = new Set(selectedClient?.connected_platforms || []);
  const eff = computeEffective(form.platforms, form.content_types);

  const setField = (key, val) => { setDirty(true); setForm(f => ({ ...f, [key]: val })); };

  const handlePlatformToggle = (pid) => {
    setDirty(true);
    setForm(f => {
      const platforms = f.platforms.includes(pid)
        ? f.platforms.filter(p => p !== pid)
        : [...f.platforms, pid];
      // Set default type when adding a platform
      const content_types = { ...f.content_types };
      if (!f.platforms.includes(pid)) {
        content_types[pid] = [CONTENT_TYPES[pid]?.[0]?.id].filter(Boolean);
      }
      return { ...f, platforms, content_types };
    });
  };

  const handleTypeToggle = (pid, typeId) => {
    setDirty(true);
    setForm(f => {
      const current = normalizeTypes(f.content_types[pid], pid);
      const next = toggleContentType(current, typeId, pid);
      return { ...f, content_types: { ...f.content_types, [pid]: next } };
    });
  };

  const handleBackdropClick = () => {
    if (!dirty) { onClose(); return; }
    if (window.confirm("¿Cancelar esta publicación? Perderás todos los datos completados.")) onClose();
  };

  useEffect(() => {
    const handler = e => {
      if (e.key !== "Escape") return;
      if (!dirty) { onClose(); return; }
      if (window.confirm("¿Cancelar esta publicación? Perderás todos los datos completados.")) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [dirty, onClose]);

  const handleSubmit = async () => {
    if (!form.client_id) { toast("Seleccioná un cliente", "error"); return; }
    if (form.platforms.length === 0) { toast("Seleccioná al menos una plataforma", "error"); return; }
    if (publishMode === "schedule" && !form.scheduled_at) { toast("Seleccioná fecha y hora", "error"); return; }
    setSaving(true);
    try {
      const payload = {
        client_id:     parseInt(form.client_id),
        platforms:     form.platforms,
        content_types: form.content_types,
        caption:       eff.caption ? (form.caption || null) : null,
        hashtags:      eff.hashtags ? (form.hashtags || null) : null,
        media_urls:    form.media_urls,
        scheduled_at:  publishMode === "schedule" ? localToUTC(form.scheduled_at) : null,
        status:        publishMode === "schedule" ? "scheduled" : "draft",
        notes:         form.notes || null,
        post_url:      eff.postUrl ? (form.post_url || null) : null,
      };
      const saved = post?.id
        ? await API.patch(`/api/social-scheduler/posts/${post.id}`, payload)
        : await API.post("/api/social-scheduler/posts", payload);

      if (publishMode === "now") {
        try {
          const pub = await API.post(`/api/social-scheduler/posts/${saved.data.id}/publish`);
          onSave({ ...saved.data, status: pub.data.status, publish_results: pub.data.results, published_at: new Date().toISOString() }, !!post?.id);
          toast(pub.data.status === "published" ? "✅ Publicado correctamente" : "⚠️ Publicado con errores", pub.data.status === "published" ? "success" : "error");
        } catch (pubErr) {
          onSave(saved.data, !!post?.id);
          toast(pubErr.response?.data?.error || "Error al publicar", "error");
        }
      } else {
        onSave(saved.data, !!post?.id);
        toast(publishMode === "schedule" ? "📅 Publicación programada" : "Borrador guardado", "success");
      }
      onClose();
    } catch (err) {
      toast(err.response?.data?.error || "Error al guardar", "error");
    } finally { setSaving(false); }
  };

  const confirmClose = () => {
    if (!dirty) { onClose(); return; }
    if (window.confirm("¿Cancelar esta publicación? Perderás todos los datos completados.")) onClose();
  };

  return (
    <div onClick={handleBackdropClick} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--bg-surface)", borderRadius: "var(--radius-xl)", border: "1px solid var(--border)",
        width: "100%", maxWidth: 580, maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)", display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 20px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
            {post?.id ? "Editar publicación" : "Nueva publicación"}
          </h2>
          <button className="btn btn-ghost btn-sm" onClick={confirmClose}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>

          {/* Cliente */}
          <div>
            <label style={labelStyle}>Cliente *</label>
            <select className="select" value={form.client_id} onChange={e => setField("client_id", e.target.value)}>
              <option value="">Seleccioná un cliente...</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.connected_platforms?.length > 0 ? ` (${c.connected_platforms.join(", ")})` : " — sin redes"}
                </option>
              ))}
            </select>
          </div>

          {/* Plataformas */}
          <div>
            <label style={labelStyle}>Plataformas *
              {form.client_id && connectedPlatforms.size > 0 && (
                <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 6 }}>(conectadas al cliente)</span>
              )}
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {PLATFORMS
                .filter(p => !form.client_id || connectedPlatforms.size === 0 || connectedPlatforms.has(p.id))
                .map(p => {
                  const sel = form.platforms.includes(p.id);
                  return (
                    <button key={p.id} type="button" onClick={() => handlePlatformToggle(p.id)} style={{
                      display: "flex", alignItems: "center", gap: 6, padding: "7px 13px",
                      borderRadius: 20, fontSize: 12, fontWeight: 600,
                      border: `1.5px solid ${sel ? p.color : "var(--border)"}`,
                      background: sel ? `${p.color}18` : "var(--bg-elevated)",
                      color: sel ? p.color : "var(--text-secondary)",
                      cursor: "pointer", transition: "all .15s", fontFamily: "inherit",
                    }}>
                      <p.Icon size={13} color={sel ? p.color : "var(--text-muted)"} />
                      {p.label}
                    </button>
                  );
                })}
            </div>
          </div>

          {/* Content types per platform */}
          {form.platforms.length > 0 && (
            <div>
              <label style={labelStyle}>Tipo de contenido</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {form.platforms.map(pid => {
                  const p = PLATFORM_MAP[pid];
                  const types = CONTENT_TYPES[pid] || [];
                  const selected = normalizeTypes(form.content_types[pid], pid);
                  const hasFeedSlot = !!FEED_SLOT[pid];
                  const feedTypes = FEED_SLOT[pid] || [];
                  const storyTypes = STORY_SLOT[pid] || [];
                  return (
                    <div key={pid} style={{ padding: "10px 12px", borderRadius: "var(--radius-md)",
                      background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        {p && <p.Icon size={13} color={p.color} />}
                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)" }}>{p?.label}</span>
                        {hasFeedSlot && (
                          <span style={{ fontSize: 10, color: "var(--text-muted)" }}>· Story se puede combinar con cualquier tipo de feed</span>
                        )}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {types.map(t => {
                          const isSel = selected.includes(t.id);
                          const isStory = storyTypes.includes(t.id);
                          const isFeed = feedTypes.includes(t.id);
                          // Radio behavior for feed types, checkbox for story
                          const isCheckbox = isStory && hasFeedSlot;
                          return (
                            <button key={t.id} type="button" onClick={() => handleTypeToggle(pid, t.id)}
                              style={{
                                display: "flex", alignItems: "center", gap: 5,
                                fontSize: 11, padding: "5px 11px", borderRadius: 12,
                                border: `1.5px solid ${isSel ? (p?.color || "var(--accent)") : "var(--border)"}`,
                                background: isSel ? `${p?.color || "var(--accent)"}18` : "var(--bg-surface)",
                                color: isSel ? (p?.color || "var(--accent)") : "var(--text-muted)",
                                cursor: "pointer", fontFamily: "inherit", fontWeight: isSel ? 700 : 400,
                                transition: "all .12s",
                              }}>
                              {/* Checkbox for story, radio dot for feed */}
                              <span style={{
                                width: 11, height: 11, flexShrink: 0,
                                borderRadius: isCheckbox ? 3 : "50%",
                                border: `2px solid ${isSel ? (p?.color || "var(--accent)") : "var(--border)"}`,
                                background: isSel ? (p?.color || "var(--accent)") : "transparent",
                                display: "flex", alignItems: "center", justifyContent: "center",
                              }}>
                                {isSel && <span style={{ color: "#fff", fontSize: 7, lineHeight: 1 }}>✓</span>}
                              </span>
                              {t.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Caption */}
          {eff.caption && (
            <div>
              <label style={labelStyle}>Caption</label>
              <textarea className="textarea" rows={4} value={form.caption}
                onChange={e => setField("caption", e.target.value)}
                placeholder="Escribí el texto del post..." />
            </div>
          )}

          {/* Hashtags */}
          {eff.hashtags && (
            <div>
              <label style={labelStyle}>Hashtags</label>
              <input className="input" value={form.hashtags}
                onChange={e => setField("hashtags", e.target.value)}
                placeholder="#agencia #marketing #digital" />
            </div>
          )}

          {/* Media upload */}
          {eff.maxMedia > 0 && (
            <div>
              <label style={labelStyle}>
                Archivos media
                {eff.maxMedia > 1 && <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 6 }}>Máx. {eff.maxMedia}</span>}
              </label>
              <MediaUploader
                mediaUrls={form.media_urls}
                onChange={updater => {
                  setDirty(true);
                  setForm(f => ({ ...f, media_urls: typeof updater === "function" ? updater(f.media_urls) : updater }));
                }}
                maxFiles={eff.maxMedia}
                accept={eff.accept}
                orderable={eff.orderable}
              />
            </div>
          )}

          {/* Publish mode toggle */}
          <div>
            <label style={labelStyle}>¿Cuándo publicar?</label>
            <div style={{ display: "flex", gap: 4, background: "var(--bg-elevated)",
              borderRadius: "var(--radius-md)", padding: 3, border: "1px solid var(--border)" }}>
              {[
                { id: "draft",    label: "💾 Borrador" },
                { id: "schedule", label: "📅 Programar" },
                { id: "now",      label: "▶ Publicar ahora" },
              ].map(opt => (
                <button key={opt.id} type="button"
                  onClick={() => { setDirty(true); setPublishMode(opt.id); }}
                  style={{
                    flex: 1, padding: "7px 4px", fontSize: 12,
                    fontWeight: publishMode === opt.id ? 700 : 400, borderRadius: "var(--radius-sm)",
                    border: "none", background: publishMode === opt.id ? "var(--bg-surface)" : "transparent",
                    color: publishMode === opt.id ? "var(--text-primary)" : "var(--text-muted)",
                    cursor: "pointer", fontFamily: "inherit",
                    boxShadow: publishMode === opt.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    transition: "all .15s", whiteSpace: "nowrap",
                  }}>{opt.label}</button>
              ))}
            </div>
            {publishMode === "schedule" && (
              <input className="input" type="datetime-local" value={form.scheduled_at}
                onChange={e => setField("scheduled_at", e.target.value)} style={{ marginTop: 10 }} />
            )}
          </div>

          {/* Post URL */}
          {eff.postUrl && (
            <div>
              <label style={labelStyle}>URL del post publicado <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>(opcional)</span></label>
              <input className="input" value={form.post_url}
                onChange={e => setField("post_url", e.target.value)}
                placeholder="https://www.instagram.com/p/..." />
            </div>
          )}

          {/* Notes */}
          <div>
            <label style={labelStyle}>Notas internas</label>
            <textarea className="textarea" rows={2} value={form.notes}
              onChange={e => setField("notes", e.target.value)}
              placeholder="Referencias de diseño, comentarios internos..." />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 20px", borderTop: "1px solid var(--border)",
          display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
          <button className="btn btn-secondary" onClick={confirmClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving
              ? (publishMode === "now" ? "Publicando..." : "Guardando...")
              : publishMode === "now" ? "▶ Guardar y publicar ahora"
              : publishMode === "schedule" ? "📅 Guardar y programar"
              : post?.id ? "Guardar cambios" : "Guardar borrador"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CSV export ────────────────────────────────────────────────────────────────
function exportToCSV(posts) {
  const headers = ["Cliente","Plataforma","Tipo de contenido","Media URLs","Caption","Hashtags","Fecha programada","Estado","URL publicado"];
  const rows = posts.map(p => {
    const plts = (p.platforms || []).map(pid => PLATFORM_MAP[pid]?.label || pid).join(" + ");
    const types = Object.entries(p.content_types || {}).flatMap(([plt, ct]) => {
      const arr = Array.isArray(ct) ? ct : [ct];
      return arr.map(c => CONTENT_TYPES[plt]?.find(t => t.id === c)?.label || c);
    }).join(" + ");
    const mediaUrls = (p.media_urls || []).join(" | ");
    return [
      p.client_name || "",
      plts,
      types,
      mediaUrls,
      p.caption || "",
      p.hashtags || "",
      p.scheduled_at ? fmtDateTime(p.scheduled_at) : "",
      STATUS_MAP[p.status]?.label || p.status || "",
      p.post_url || "",
    ];
  });
  const csv = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell || "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `social-scheduler-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Main component ────────────────────────────────────────────────────────────
export default function SocialScheduler() {
  const toast = useToast();
  const isMobile = useIsMobile();

  const [posts, setPosts] = useState([]);
  const [connectedClients, setConnectedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");

  // Filters
  const [filterClient, setFilterClient] = useState("");
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  // Pagination
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Calendar
  const now = new Date();
  const [calYear, setCalYear]   = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);

  // Modal
  const [editingPost, setEditingPost] = useState(null);
  const [modalOpen, setModalOpen]     = useState(false);

  useEffect(() => {
    API.get("/api/social-scheduler/clients").then(r => setConnectedClients(r.data)).catch(() => {});
  }, []);

  // Build shared filter params (used by loadPosts and export)
  const buildParams = useCallback((extra = {}) => {
    const params = new URLSearchParams();
    if (filterClient)   params.set("client_id", filterClient);
    if (filterPlatform) params.set("platform", filterPlatform);
    if (filterStatus)   params.set("status", filterStatus);
    if (filterType)     params.set("content_type", filterType);
    if (filterFrom)     params.set("from", new Date(filterFrom).toISOString());
    if (filterTo) {
      const to = new Date(filterTo);
      to.setHours(23, 59, 59);
      params.set("to", to.toISOString());
    }
    if (view === "calendar") {
      params.set("from", new Date(calYear, calMonth, 1).toISOString());
      params.set("to", new Date(calYear, calMonth + 1, 0, 23, 59, 59).toISOString());
    }
    Object.entries(extra).forEach(([k, v]) => params.set(k, v));
    return params;
  }, [filterClient, filterPlatform, filterStatus, filterType, filterFrom, filterTo, view, calYear, calMonth]);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams({ page, limit: PAGE_SIZE });
      const r = await API.get(`/api/social-scheduler/posts?${params}`);
      setPosts(r.data.rows);
      setTotal(r.data.total);
    } catch (err) {
      toast(err.response?.data?.error || "Error al cargar publicaciones", "error");
    } finally { setLoading(false); }
  }, [buildParams, page, toast]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = buildParams({ all: "true" });
      const r = await API.get(`/api/social-scheduler/posts?${params}`);
      exportToCSV(r.data.rows);
    } catch (err) {
      toast("Error al exportar", "error");
    } finally { setExporting(false); }
  };

  // Reset to page 1 when any filter changes
  useEffect(() => { setPage(1); }, [filterClient, filterPlatform, filterStatus, filterType, filterFrom, filterTo, view, calYear, calMonth]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const handleSave = (savedPost, isEdit) => {
    setPosts(prev => isEdit ? prev.map(p => p.id === savedPost.id ? savedPost : p) : [savedPost, ...prev]);
  };
  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta publicación? Se eliminará permanentemente junto con todos sus archivos media.")) return;
    try {
      await API.delete(`/api/social-scheduler/posts/${id}`);
      // Only remove from UI after confirmed server-side deletion
      setPosts(prev => prev.filter(p => p.id !== id));
      setTotal(prev => Math.max(0, prev - 1));
      toast("Publicación eliminada", "success");
    } catch (err) {
      toast(err.response?.data?.error || "Error al eliminar — la publicación NO fue eliminada", "error");
    }
  };
  const handleUpdate = (updated) => setPosts(prev => prev.map(p => p.id === updated.id ? updated : p));
  const openEdit  = (post) => { setEditingPost(post); setModalOpen(true); };
  const openNew   = () => { setEditingPost({}); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditingPost(null); };

  const prevMonth = () => { setSelectedDay(null); if (calMonth === 0) { setCalYear(y=>y-1); setCalMonth(11); } else setCalMonth(m=>m-1); };
  const nextMonth = () => { setSelectedDay(null); if (calMonth === 11) { setCalYear(y=>y+1); setCalMonth(0); } else setCalMonth(m=>m+1); };

  const dayPosts = selectedDay ? posts.filter(p => {
    if (!p.scheduled_at) return false;
    const d = new Date(p.scheduled_at);
    return d.getFullYear()===calYear && d.getMonth()===calMonth && d.getDate()===selectedDay;
  }) : [];

  // All content type options for the filter (flat list)
  const allTypeOptions = [];
  const seen = new Set();
  for (const pid of (filterPlatform ? [filterPlatform] : Object.keys(CONTENT_TYPES))) {
    for (const t of (CONTENT_TYPES[pid] || [])) {
      if (!seen.has(t.id)) { seen.add(t.id); allTypeOptions.push(t); }
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">📆 Social Scheduler</h1>
        <p className="page-subtitle">Planificá y publicá en redes sociales desde Smith</p>
      </div>

      <div className="page-body">
        {/* Controls */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
          <select className="select" value={filterClient} onChange={e => setFilterClient(e.target.value)} style={{ width: "auto", minWidth: 140 }}>
            <option value="">Todos los clientes</option>
            {connectedClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select className="select" value={filterPlatform} onChange={e => { setFilterPlatform(e.target.value); setFilterType(""); }} style={{ width: "auto", minWidth: 130 }}>
            <option value="">Todas las plataformas</option>
            {PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>

          <select className="select" value={filterType} onChange={e => setFilterType(e.target.value)} style={{ width: "auto", minWidth: 140 }}>
            <option value="">Todos los tipos</option>
            {allTypeOptions.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>

          <select className="select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: "auto", minWidth: 130 }}>
            <option value="">Todos los estados</option>
            {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>

          {/* Date range */}
          <input className="input" type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
            style={{ width: "auto", minWidth: 130 }} title="Desde" />
          <input className="input" type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
            style={{ width: "auto", minWidth: 130 }} title="Hasta" />
          {(filterFrom || filterTo) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setFilterFrom(""); setFilterTo(""); }}
              title="Limpiar fechas">✕</button>
          )}

          <div style={{ flex: 1 }} />

          {/* View toggle */}
          <div style={{ display: "flex", gap: 2, background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", padding: 3, border: "1px solid var(--border)", flexShrink: 0 }}>
            {[["list","📋 Lista"],["calendar","📅 Calendario"]].map(([id,lbl]) => (
              <button key={id} onClick={() => setView(id)} style={{
                padding: "5px 12px", fontSize: 12, fontWeight: 500, borderRadius: "var(--radius-sm)",
                border: "none", background: view===id ? "var(--bg-surface)" : "transparent",
                color: view===id ? "var(--text-primary)" : "var(--text-muted)",
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: view===id ? "0 1px 3px rgba(0,0,0,0.1)" : "none", transition: "all .15s",
              }}>{lbl}</button>
            ))}
          </div>

          <button className="btn btn-secondary" onClick={handleExport} disabled={exporting || posts.length === 0}
            style={{ flexShrink: 0 }} title="Exportar todo lo filtrado a CSV">
            {exporting ? "Exportando..." : "⬇ CSV"}
          </button>
          <button className="btn btn-primary" onClick={openNew} style={{ flexShrink: 0 }}>+ Nueva publicación</button>
        </div>

        {/* No connected clients */}
        {!loading && connectedClients.length === 0 && (
          <div className="card" style={{ textAlign: "center", padding: "48px 24px" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔌</div>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Sin perfiles sociales conectados</h3>
            <p style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 20 }}>
              Conectá al menos una red social en el perfil de un cliente para usar el Scheduler.
            </p>
            <a href="/clients" className="btn btn-primary" style={{ textDecoration: "none", display: "inline-flex" }}>
              Ir a Clientes
            </a>
          </div>
        )}

        {/* ── LIST VIEW — TABLE ── */}
        {connectedClients.length > 0 && view === "list" && (
          <div>
            {loading ? (
              <div style={{ textAlign: "center", padding: 48 }}><div className="spinner" /></div>
            ) : posts.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 24px" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
                <p style={{ color: "var(--text-muted)", fontSize: 14 }}>No hay publicaciones con los filtros actuales.</p>
                <button className="btn btn-primary" onClick={openNew} style={{ marginTop: 16 }}>+ Crear publicación</button>
              </div>
            ) : (
              <>
              <div style={{ overflowX: "auto", borderRadius: "var(--radius-lg)", border: "1px solid var(--border)" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                  <thead>
                    <tr style={{ background: "var(--bg-elevated)" }}>
                      {["Cliente","Plataforma","Tipo","Media","Caption / Hashtags","Fecha","Estado","Notas","Acciones"].map(h => (
                        <th key={h} style={{
                          padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)",
                          borderBottom: "1px solid var(--border)", whiteSpace: "nowrap",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map(p => (
                      <PostRow key={p.id} post={p} onEdit={openEdit} onDelete={handleDelete} onUpdate={handleUpdate} />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                  marginTop: 16, flexWrap: "wrap", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {total} publicación{total !== 1 ? "es" : ""} · Página {page} de {totalPages}
                  </span>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setPage(1)} disabled={page === 1}>«</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}>‹</button>
                    {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                      // Show pages around current page
                      let p;
                      if (totalPages <= 7) p = i + 1;
                      else if (page <= 4) p = i + 1;
                      else if (page >= totalPages - 3) p = totalPages - 6 + i;
                      else p = page - 3 + i;
                      return (
                        <button key={p} className="btn btn-ghost btn-sm" onClick={() => setPage(p)}
                          style={{ fontWeight: page === p ? 700 : 400, minWidth: 32,
                            background: page === p ? "var(--accent-dim)" : undefined,
                            color: page === p ? "var(--accent)" : undefined }}>
                          {p}
                        </button>
                      );
                    })}
                    <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}>›</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
                  </div>
                </div>
              )}
              {totalPages <= 1 && total > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: "var(--text-muted)", textAlign: "right" }}>
                  {total} publicación{total !== 1 ? "es" : ""}
                </div>
              )}
              </>
            )}
          </div>
        )}

        {/* ── CALENDAR VIEW ── */}
        {connectedClients.length > 0 && view === "calendar" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, justifyContent: "center" }}>
              <button className="btn btn-ghost btn-sm" onClick={prevMonth}>‹</button>
              <span style={{ fontSize: 15, fontWeight: 600, minWidth: 180, textAlign: "center" }}>
                {MONTHS_ES[calMonth]} {calYear}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={nextMonth}>›</button>
            </div>
            <div className="card" style={{ padding: "16px" }}>
              {loading ? <div style={{ textAlign: "center", padding: 40 }}><div className="spinner" /></div> : (
                <CalendarView posts={posts} year={calYear} month={calMonth}
                  selectedDay={selectedDay}
                  onDayClick={d => setSelectedDay(prev => prev === d ? null : d)}
                  onPostClick={openEdit}
                />
              )}
            </div>
            {selectedDay && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: ".07em", color: "var(--text-muted)", marginBottom: 12 }}>
                  {selectedDay} de {MONTHS_ES[calMonth]} — {dayPosts.length} publicación{dayPosts.length !== 1 ? "es" : ""}
                </div>
                {dayPosts.length === 0 ? (
                  <p style={{ fontSize: 13, color: "var(--text-muted)" }}>Sin publicaciones para este día.</p>
                ) : (
                  <div style={{ overflowX: "auto", borderRadius: "var(--radius-lg)", border: "1px solid var(--border)" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                      <thead>
                        <tr style={{ background: "var(--bg-elevated)" }}>
                          {["Cliente","Plataforma","Tipo","Media","Caption / Hashtags","Fecha","Estado","Notas","Acciones"].map(h => (
                            <th key={h} style={{ padding: "8px 14px", textAlign: "left", fontSize: 11, fontWeight: 700,
                              textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)",
                              borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dayPosts.map(p => (
                          <PostRow key={p.id} post={p} onEdit={openEdit} onDelete={handleDelete} onUpdate={handleUpdate} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {modalOpen && (
        <PostModal post={editingPost} clients={connectedClients} onClose={closeModal} onSave={handleSave} />
      )}
    </div>
  );
}
